import type { BadgeColor } from './badge'

/**
 * Shared types + pure date helpers for `EventCalendar`. All date math is
 * **local-time** (native `Date`), matching how a scheduling UI is read — no
 * timezone library, no mutation (every helper returns a fresh `Date`).
 */

/** The three time scales the calendar can render. */
export type CalendarView = 'month' | 'week' | 'day'

/**
 * Event accent color — the categorical badge palette, so an event's chip,
 * dot, and time-grid block all resolve through the same `badge-colors` maps as
 * the rest of the design system.
 */
export type CalendarEventColor = BadgeColor

export interface CalendarEvent {
  id: string
  title: string
  /** Start datetime (local). For an all-day event only the date part matters. */
  start: Date
  /** Optional end datetime (local); exclusive-ish, treated as "until". */
  end?: Date
  /** All-day event — rendered in the all-day row, not the time grid. */
  allDay?: boolean
  /** Accent color (defaults to `primary` when omitted). */
  color?: CalendarEventColor
  /** Free-text notes shown in the add/edit modal. */
  description?: string
  /** Optional location, shown on `EventCard`. */
  location?: string
}

/** 0 = Sunday, 1 = Monday. */
export type WeekStart = 0 | 1

export const MINUTES_PER_DAY = 24 * 60

// ── construction / arithmetic ────────────────────────────────────────────────

export const startOfDay = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate())

export const addDays = (d: Date, n: number): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)

export const addMonths = (d: Date, n: number): Date =>
  new Date(d.getFullYear(), d.getMonth() + n, d.getDate())

export const startOfMonth = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), 1)

/** Start of the week containing `d`, honoring `weekStartsOn`. */
export const startOfWeek = (d: Date, weekStartsOn: WeekStart): Date => {
  const day = d.getDay()
  const diff = (day - weekStartsOn + 7) % 7
  return addDays(startOfDay(d), -diff)
}

// ── comparison ───────────────────────────────────────────────────────────────

export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

export const isSameMonth = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()

// ── grids ────────────────────────────────────────────────────────────────────

/** The 42 days (6 weeks) covering the month of `date`, for the month grid. */
export const getMonthGrid = (date: Date, weekStartsOn: WeekStart): Date[] => {
  const start = startOfWeek(startOfMonth(date), weekStartsOn)
  return Array.from({ length: 42 }, (_, i) => addDays(start, i))
}

/** The 7 days of the week containing `date`. */
export const getWeekDays = (date: Date, weekStartsOn: WeekStart): Date[] => {
  const start = startOfWeek(date, weekStartsOn)
  return Array.from({ length: 7 }, (_, i) => addDays(start, i))
}

/** Whole-day difference `a − b` (ignoring time), for column math. */
export const dayDiff = (a: Date, b: Date): number =>
  Math.round((startOfDay(a).getTime() - startOfDay(b).getTime()) / 86_400_000)

/**
 * True for an event that genuinely spans multiple days — it must both cross a
 * calendar-day boundary AND last at least a full day. This keeps a short event
 * that merely straddles midnight (e.g. 11:30 PM–12:30 AM) a *timed* event
 * (clamped per day by `dayEventSpan`) rather than an all-day-style bar.
 */
export const isMultiDay = (event: CalendarEvent): boolean => {
  if (!event.end) return false
  const crossesDayBoundary = dayDiff(event.end, event.start) > 0
  const spansAtLeastADay =
    event.end.getTime() - event.start.getTime() >= MINUTES_PER_DAY * 60_000
  return crossesDayBoundary && spansAtLeastADay
}

/**
 * Events that render as a horizontal bar rather than a time block: all-day
 * events, and any event spanning a full day or more (e.g. a conference or OOO).
 */
export const isBarEvent = (event: CalendarEvent): boolean =>
  !!event.allDay || isMultiDay(event)

export interface SpanningSegment {
  event: CalendarEvent
  /** 0-based column index (into `days`) where the bar starts. */
  startIndex: number
  /** 0-based column index where the bar ends (inclusive). */
  endIndex: number
  /** Vertical lane (row) so overlapping bars don't collide. */
  lane: number
  /** The event began before this run of days (square the left edge). */
  continuesBefore: boolean
  /** The event ends after this run of days (square the right edge). */
  continuesAfter: boolean
}

/**
 * Pack bar-events overlapping a contiguous, ascending run of `days` (a month
 * week, or the week/day columns) into lanes via greedy first-fit, so multi-day
 * and all-day events render as non-overlapping horizontal bars that line up
 * across the row. Each segment is clamped to the run and flagged when it
 * continues past either edge.
 */
export const layoutBarEvents = (
  events: CalendarEvent[],
  days: Date[]
): { segments: SpanningSegment[]; laneCount: number } => {
  const rangeStart = startOfDay(days[0])
  const rangeEnd = startOfDay(days[days.length - 1])
  const bars = events
    .filter(isBarEvent)
    .filter((e) => {
      const s = startOfDay(e.start)
      const en = startOfDay(e.end ?? e.start)
      return s <= rangeEnd && en >= rangeStart
    })
    .sort((a, b) => {
      const byStart = a.start.getTime() - b.start.getTime()
      if (byStart !== 0) return byStart
      // Longer events first — tidier packing.
      return dayDiff(b.end ?? b.start, b.start) - dayDiff(a.end ?? a.start, a.start)
    })

  const laneEnds: number[] = [] // last occupied endIndex per lane
  const segments: SpanningSegment[] = []
  for (const event of bars) {
    const sDay = startOfDay(event.start)
    const eDay = startOfDay(event.end ?? event.start)
    const startIndex = Math.max(0, dayDiff(sDay, rangeStart))
    const endIndex = Math.min(days.length - 1, dayDiff(eDay, rangeStart))
    let lane = 0
    while (lane < laneEnds.length && laneEnds[lane] >= startIndex) lane++
    laneEnds[lane] = endIndex
    segments.push({
      event,
      startIndex,
      endIndex,
      lane,
      continuesBefore: sDay < rangeStart,
      continuesAfter: eDay > rangeEnd,
    })
  }
  return { segments, laneCount: laneEnds.length }
}

// ── time positioning ─────────────────────────────────────────────────────────

/** Minutes elapsed since local midnight (0–1439), for time-grid placement. */
export const minutesSinceMidnight = (d: Date): number =>
  d.getHours() * 60 + d.getMinutes()

/**
 * The vertical span an event occupies within a single day column, clamped to
 * that day. Returns minute offsets from midnight; `end` falls back to a 30-min
 * block when the event has no explicit end.
 */
export const dayEventSpan = (
  event: CalendarEvent,
  day: Date
): { startMin: number; endMin: number } => {
  const dayStart = startOfDay(day)
  const dayEnd = addDays(dayStart, 1)
  const start = event.start < dayStart ? dayStart : event.start
  const rawEnd = event.end ?? new Date(start.getTime() + 30 * 60_000)
  const end = rawEnd > dayEnd ? dayEnd : rawEnd
  const startMin = minutesSinceMidnight(start)
  // A same-instant end gets a 30-min block; a midnight end (genuine or clamped
  // to the next day) fills the rest of the day rather than reading as 0.
  const endMin =
    end.getTime() <= start.getTime()
      ? startMin + 30
      : minutesSinceMidnight(end) === 0
        ? MINUTES_PER_DAY
        : minutesSinceMidnight(end)
  return { startMin, endMin }
}

export interface TimedLayoutItem {
  event: CalendarEvent
  /** Minute offsets within the day (from `dayEventSpan`). */
  startMin: number
  endMin: number
  /** Column index within this event's overlap cluster. */
  lane: number
  /** Total columns in this event's overlap cluster (for width = 1/columns). */
  columns: number
}

/**
 * Lay timed events for a single day into side-by-side columns so overlapping
 * events sit next to each other instead of stacking (and hiding the one behind).
 * Events are grouped into clusters of transitively-overlapping blocks; within a
 * cluster each event gets a column via greedy first-fit and every event shares
 * the cluster's column count so widths align (à la Google Calendar / Outlook).
 */
export const layoutDayTimedEvents = (
  events: CalendarEvent[],
  day: Date
): TimedLayoutItem[] => {
  const items: TimedLayoutItem[] = events
    .map((event) => {
      const { startMin, endMin } = dayEventSpan(event, day)
      return { event, startMin, endMin, lane: 0, columns: 1 }
    })
    .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin)

  const result: TimedLayoutItem[] = []
  let cluster: TimedLayoutItem[] = []
  let clusterEnd = -Infinity

  const flush = () => {
    if (cluster.length === 0) return
    const laneEnds: number[] = [] // endMin of the last event placed in each lane
    for (const it of cluster) {
      let lane = 0
      while (lane < laneEnds.length && laneEnds[lane] > it.startMin) lane++
      laneEnds[lane] = it.endMin
      it.lane = lane
    }
    for (const it of cluster) {
      it.columns = laneEnds.length
      result.push(it)
    }
    cluster = []
  }

  for (const it of items) {
    // A block that starts at/after everything so far in the cluster starts a
    // fresh cluster (no overlap → full width).
    if (cluster.length > 0 && it.startMin >= clusterEnd) {
      flush()
      clusterEnd = -Infinity
    }
    cluster.push(it)
    clusterEnd = Math.max(clusterEnd, it.endMin)
  }
  flush()
  return result
}

/** True when `event` intersects the calendar day `day` (all-day or timed). */
export const eventOnDay = (event: CalendarEvent, day: Date): boolean => {
  const dayStart = startOfDay(day)
  const dayEnd = addDays(dayStart, 1)
  const end = event.end ?? event.start
  return event.start < dayEnd && end >= dayStart
}

/** Events intersecting `day`, sorted all-day first then by start time. */
export const eventsForDay = (
  events: CalendarEvent[],
  day: Date
): CalendarEvent[] =>
  events
    .filter((e) => eventOnDay(e, day))
    .sort((a, b) => {
      if (!!a.allDay !== !!b.allDay) return a.allDay ? -1 : 1
      return a.start.getTime() - b.start.getTime()
    })

// ── formatting ───────────────────────────────────────────────────────────────

const fmt = (opts: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('en-US', opts)

const monthYearFmt = fmt({ month: 'long', year: 'numeric' })
const dayTitleFmt = fmt({ weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
const weekdayShortFmt = fmt({ weekday: 'short' })
const weekdayLongFmt = fmt({ weekday: 'long' })
const monthDayFmt = fmt({ month: 'short', day: 'numeric' })
const timeFmt = fmt({ hour: 'numeric', minute: '2-digit' })
const hourFmt = fmt({ hour: 'numeric' })
const fullDateFmt = fmt({ weekday: 'short', month: 'short', day: 'numeric' })

export const formatMonthYear = (d: Date): string => monthYearFmt.format(d)
export const formatDayTitle = (d: Date): string => dayTitleFmt.format(d)
export const formatWeekdayShort = (d: Date): string => weekdayShortFmt.format(d)
export const formatWeekdayLong = (d: Date): string => weekdayLongFmt.format(d)
export const formatTime = (d: Date): string => timeFmt.format(d)
export const formatFullDate = (d: Date): string => fullDateFmt.format(d)

/** Single narrow letter for the weekday header (e.g. "M"), for mobile. */
export const formatWeekdayNarrow = (d: Date): string =>
  weekdayShortFmt.format(d).charAt(0)

/** The label for the hour row at `hour` (0–23), e.g. "9 AM". */
export const formatHour = (hour: number): string =>
  hourFmt.format(new Date(2000, 0, 1, hour))

/** Compact range label for a week, e.g. "Jul 6 – 12, 2026". */
export const formatWeekRange = (days: Date[]): string => {
  const first = days[0]
  const last = days[days.length - 1]
  const year = last.getFullYear()
  if (first.getMonth() === last.getMonth()) {
    return `${monthDayFmt.format(first)} – ${last.getDate()}, ${year}`
  }
  return `${monthDayFmt.format(first)} – ${monthDayFmt.format(last)}, ${year}`
}

/**
 * A human range label for an event, used in the modal header:
 * "All day", "9:00 AM – 10:00 AM", or a multi-day span.
 */
export const formatEventRange = (event: CalendarEvent): string => {
  if (event.allDay) {
    if (event.end && !isSameDay(event.start, event.end)) {
      return `${formatFullDate(event.start)} – ${formatFullDate(event.end)} · All day`
    }
    return `${formatFullDate(event.start)} · All day`
  }
  const startLabel = `${formatFullDate(event.start)}, ${formatTime(event.start)}`
  if (!event.end) return startLabel
  if (isSameDay(event.start, event.end)) {
    return `${startLabel} – ${formatTime(event.end)}`
  }
  return `${startLabel} – ${formatFullDate(event.end)}, ${formatTime(event.end)}`
}
